import { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connectSocket } from '../../utils/socket';
import {
  BookOpen, Moon, Heart, MapPin, Star, Clock, Shield, Users, Landmark, Leaf,
  Swords,
} from 'lucide-react';

const LEARN_TOPICS = [
  {
    Icon: Clock,
    title: 'Gebet (Salat)',
    text: 'Die fünf täglichen Gebete sind eine der fünf Säulen des Islam. Sie verbinden den Gläubigen täglich mit Allah — beim Morgengrauen, Mittag, Nachmittag, Sonnenuntergang und in der Nacht.',
  },
  {
    Icon: BookOpen,
    title: 'Der Heilige Quran',
    text: 'Der Quran ist das Wort Allahs, offenbart dem Propheten Muhammad ﷺ über 23 Jahre. Er besteht aus 114 Suren und ist die unveränderliche Grundlage des islamischen Glaubens und Lebens.',
  },
  {
    Icon: Moon,
    title: 'Ramadan & Fasten',
    text: 'Der Ramadan ist der neunte Monat des islamischen Kalenders. Muslime weltweit fasten von Sonnenaufgang bis Sonnenuntergang — als Akt der Dankbarkeit, Selbstdisziplin und Nähe zu Allah.',
  },
  {
    Icon: Heart,
    title: 'Zakat — Sozialpflicht',
    text: 'Zakat ist die dritte Säule des Islam. Wer Vermögen über dem Nisab-Betrag besitzt, gibt 2,5 % davon jährlich an Bedürftige. Dies reinigt das Vermögen und stärkt die Gemeinschaft.',
  },
  {
    Icon: MapPin,
    title: 'Hajj — Pilgerfahrt',
    text: 'Die Pilgerfahrt nach Mekka ist die fünfte Säule des Islam und einmal im Leben Pflicht für jeden Muslim, der es gesundheitlich und finanziell kann. Millionen Menschen versammeln sich jährlich an der Kaaba.',
  },
  {
    Icon: Star,
    title: 'Shahada — Glaubensbekenntnis',
    text: 'Die Shahada — "Ich bezeuge, dass es keinen Gott außer Allah gibt und dass Muhammad Sein Gesandter ist" — ist die erste Säule des Islam und das Fundament des muslimischen Glaubens.',
  },
  {
    Icon: Shield,
    title: 'Tauhid — Einheit Gottes',
    text: 'Tauhid bezeichnet den absoluten Monotheismus im Islam — den uneingeschränkten Glauben an die Einzigartigkeit Allahs. Er ist der Kern der islamischen Theologie und des gesamten islamischen Denkens.',
  },
  {
    Icon: Users,
    title: 'Die Prophetengeschichte',
    text: 'Allah sandte über die Jahrtausende viele Propheten — darunter Ibrahim, Musa, Isa und als letzten Muhammad ﷺ. Sie alle lehrten denselben Glauben an den einen Gott und riefen zur Gerechtigkeit auf.',
  },
  {
    Icon: Landmark,
    title: 'Islamische Geschichte',
    text: 'Im Goldenen Zeitalter des Islam (8.–13. Jh.) blühten Wissenschaft, Medizin, Mathematik und Philosophie. Muslimische Gelehrte bewahrten antikes Wissen und legten Grundlagen der modernen Zivilisation.',
  },
  {
    Icon: Leaf,
    title: 'Adab — Islamisches Benehmen',
    text: 'Der Prophet Muhammad ﷺ sagte: "Ich wurde gesandt, um den guten Charakter zu vervollkommnen." Ehrlichkeit, Respekt, Mitgefühl und Großzügigkeit sind zentrale Tugenden des muslimischen Lebens.',
  },
];

export default function LobbyPage() {
  const { sessionId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const socketRef = useRef(null);
  const participantIdRef = useRef(null);
  const navigateFlagsRef = useRef(null);
  const learnTimer = useRef(null);

  const participant = state?.participant || JSON.parse(localStorage.getItem('zq_participant') || '{}');
  const quiz = state?.quiz;

  const enableLearningMode = quiz?.enableLearningMode || quiz?.enable_learning_mode || false;

  const [participants, setParticipants] = useState([]);
  const [isDuel, setIsDuel] = useState(false);
  const [duelMosqueName, setDuelMosqueName] = useState('');
  const [mosqueName, setMosqueName] = useState('');
  const [learnIdx, setLearnIdx] = useState(0);

  const myTeam = participant.team || null;

  useEffect(() => {
    if (!enableLearningMode) return;
    learnTimer.current = setInterval(() => {
      setLearnIdx(prev => (prev + 1) % LEARN_TOPICS.length);
    }, 5000);
    return () => clearInterval(learnTimer.current);
  }, [enableLearningMode]);

  useEffect(() => {
    const socket = connectSocket();
    socketRef.current = socket;

    const doJoin = () => socket.emit('competitor:join', {
      sessionId,
      firstName: participant.firstName,
      lastName: participant.lastName,
      language: participant.language || localStorage.getItem('zq_lang') || 'de',
      ageGroupLabel: participant.ageGroupLabel,
      team: participant.team || null,
    });
    socket.on('connect', doJoin);
    if (socket.connected) doJoin();

    socket.on('joined', ({ participantId }) => {
      participantIdRef.current = participantId;
    });

    socket.on('lobby:update', ({ participants: p }) => setParticipants(p));

    socket.on('quiz:started', (flags) => {
      if (flags.isDuel) setIsDuel(true);
      if (flags.duelMosqueName) setDuelMosqueName(flags.duelMosqueName);
      if (flags.mosqueName) setMosqueName(flags.mosqueName);

      if (learnTimer.current) clearInterval(learnTimer.current);

      navigate(`/quiz/${sessionId}`, {
        state: {
          quiz, participant,
          participantId: participantIdRef.current,
          bgColor: flags.bgColor || 'dark',
          bgImage: flags.bgImage || null,
          enable5050: flags.enable5050 ?? false,
          enableStreak: flags.enableStreak ?? false,
          showAnswerImmediately: flags.showAnswerImmediately ?? true,
          enableFamilyMode: flags.enableFamilyMode ?? false,
          isDuel: flags.isDuel ?? false,
          duelMosqueName: flags.duelMosqueName || null,
          mosqueName: flags.mosqueName || null,
          logoUrl: flags.logoUrl || null,
          customColor: flags.customColor || null,
        },
      });
    });

    socket.on('error', ({ message }) => {
      alert(message);
      navigate('/join');
    });

    return () => {
      socket.off('joined');
      socket.off('lobby:update');
      socket.off('quiz:started');
      socket.off('error');
    };
  }, [sessionId, navigate, participant, quiz]);

  const topic = LEARN_TOPICS[learnIdx];
  const TopicIcon = topic.Icon;

  if (enableLearningMode) {
    return (
      <div style={{ minHeight: '100vh', background: '#0D2B1F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 520, width: '100%' }}>

          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 11, color: '#5B8A7599', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8 }}>
              Lernmodus — Quiz startet gleich
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
              <span style={{ fontSize: 13, color: '#5B8A7577' }}>
                {participant.firstName} {participant.lastName} · {participants.length} Teilnehmer
              </span>
            </div>
          </div>

          <div style={{
            background: '#153D2C', border: '1px solid #1F5039', borderRadius: 20,
            padding: '40px 36px', textAlign: 'center',
            transition: 'opacity 0.4s',
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: 18,
              background: '#1F5039', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
            }}>
              <TopicIcon size={36} color="#5B8A75" strokeWidth={1.5} />
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#FFFFFF', marginBottom: 14 }}>{topic.title}</h2>
            <p style={{ fontSize: 15, color: '#FFFFFF99', lineHeight: 1.7, maxWidth: 420, margin: '0 auto' }}>{topic.text}</p>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
            {LEARN_TOPICS.map((_, i) => (
              <div
                key={i}
                onClick={() => setLearnIdx(i)}
                style={{
                  width: i === learnIdx ? 20 : 8, height: 8, borderRadius: 4,
                  background: i === learnIdx ? '#5B8A75' : '#1F5039',
                  transition: 'all 0.3s', cursor: 'pointer',
                }}
              />
            ))}
          </div>

          {isDuel && duelMosqueName && (
            <div style={{ marginTop: 24, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#5B8A7599', fontSize: 13 }}>
              <Swords size={14} color="#5B8A75" />
              <span>Duell gegen <strong style={{ color: '#5B8A75' }}>{duelMosqueName}</strong></span>
            </div>
          )}
        </div>
      </div>
    );
  }

  const welcomeMessage = quiz?.welcomeMessage || quiz?.welcome_message || null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 480, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontFamily: 'Playfair Display', fontSize: 28, color: 'var(--text)' }}>
            {mosqueName || <><span style={{ color: 'var(--primary-dk)' }}>Ilmi</span><span style={{ color: 'var(--accent)' }}>fy</span></>}
          </h1>
          <div style={{ marginTop: 16, padding: '8px 20px', background: 'var(--primary-lt)', borderRadius: 100, display: 'inline-block' }}>
            <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{participant.firstName} {participant.lastName}</span>
            {myTeam && <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>· {myTeam}</span>}
          </div>
          {isDuel && duelMosqueName && (
            <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Swords size={13} />
              <span>Duell gegen <strong>{duelMosqueName}</strong></span>
            </div>
          )}
        </div>

        {welcomeMessage && (
          <div style={{
            marginBottom: 16, padding: '14px 18px', borderRadius: 12,
            background: 'var(--primary-lt)', border: '1px solid var(--primary)',
            textAlign: 'center', fontSize: 15, color: 'var(--primary)', fontWeight: 500,
            lineHeight: 1.5,
          }}>
            {welcomeMessage}
          </div>
        )}

        <div className="card" style={{ textAlign: 'center', marginBottom: 20 }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: 20, color: 'var(--text)' }}>{t('quiz.waitingForStart')}</h2>
          <p style={{ color: 'var(--text3)', marginTop: 8 }}>
            {participants.length} {t('quiz.participants')}
          </p>
        </div>

        <div className="card">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {participants.map((p, i) => (
              <div key={i} style={{
                padding: '4px 12px',
                background: p.firstName === participant.firstName && p.lastName === participant.lastName
                  ? 'var(--primary)' : 'var(--primary-lt)',
                color: p.firstName === participant.firstName && p.lastName === participant.lastName
                  ? 'white' : 'var(--primary)',
                borderRadius: 100, fontSize: 13, fontWeight: 500,
              }}>
                {p.firstName} {p.lastName}
                {p.team && <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 4 }}>({p.team})</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
